import { useEffect } from "react";
import {
  joinSocketRoom,
  onSocketEvent,
  orderFileRoom,
  useFiles,
  type File as SharedFile,
} from "@jasper/shared";
import { FileType } from "/@/generated/graphql";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { useQrCodeCanvas } from "/@/components/order/useQrCodeCanvas";

const ORDER_FILE_TYPES: string[] = [
  FileType.BillingDentistTraceability,
  FileType.BillingPatientTraceability,
  FileType.ClientFile,
  FileType.OrderAddonFile,
];

type UseOrderFilesManagementParams = {
  order: { id: string; patient?: { lastName?: string | null } | null };
};

// Order files: list, download, delete, and add from the computer or a phone (QR code).
export const useOrderFilesManagement = ({
  order,
}: UseOrderFilesManagementParams) => {
  const { user } = useAuthContext();
  const qrCodeCanvasRef = useQrCodeCanvas(
    user?.accessToken
      ? `${window.location.origin}/#/order-upload-file/${order.id}/${order.patient?.lastName}/${user.accessToken}/${FileType.OrderAddonFile}`
      : undefined
  );
  const {
    getPresignedDownloadUrl,
    uploadFileToS3,
    filesOfOrder,
    refetchFilesOfOrder,
    deleteOneFile,
  } = useFiles(order.id);

  useEffect(() => {
    const leaveRoom = joinSocketRoom(orderFileRoom(order.id));
    const unsubscribe = onSocketEvent("clientOrderFile", () => {
      refetchFilesOfOrder();
    });

    return () => {
      unsubscribe();
      leaveRoom();
    };
  }, []);

  const files: SharedFile[] = (filesOfOrder?.getFilesOfOrder ?? []).filter(
    (file: SharedFile) => ORDER_FILE_TYPES.includes(file.fileType)
  );

  return {
    qrCodeCanvasRef,
    files,
    uploadFiles: uploadFileToS3,
    deleteFile: deleteOneFile,
    downloadFile: getPresignedDownloadUrl,
  };
};
